import { useState } from "react";
import { createRootRouteWithContext, Link, Outlet, useLocation } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { accessAPI, queries, isUnauthorized, errorMessage } from "../lib/api";
import { AppearanceSelect } from "../lib/appearance";
import { NavIcon, Drawer } from "../components/Workspace";
import { styles } from "../lib/styles";
export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: Shell
});
function Shell() {
  const location = useLocation();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const me = useQuery(queries.me());
  const logout = useMutation({
    mutationFn: accessAPI.logout,
    onSuccess: () => {
      window.location.assign("/");
    }
  });
  if (location.pathname === "/approve") return <Outlet />;
  if (me.isPending) return <main {...stylex.props(styles.main)} aria-busy="true">
        Checking your session…
      </main>;
  if (me.error && !isUnauthorized(me.error)) return <main {...stylex.props(styles.main)}>
        <h1 {...stylex.props(styles.title)}>Workspace unavailable</h1>
        <p role="alert" {...stylex.props(styles.error)}>
          Your session could not be checked. This may be a temporary service or connection problem.
        </p>
        <Button label="Try again" isLoading={me.isFetching} onClick={() => {
      void me.refetch();
    }} />
      </main>;
  if (!me.data || isUnauthorized(me.error)) return <div {...stylex.props(styles.loginShell)}>
        <main {...stylex.props(styles.login)}>
          <section data-workspace-panel {...stylex.props(styles.panel)}>
            <div {...stylex.props(styles.brand, styles.loginBrand)}>
              <img src="/ssbd-logo.png" width="48" height="48" alt="SSBD" />
              <span>
                SSBD Access
                <span {...stylex.props(styles.brandSubtitle)}>Your access workspace</span>
              </span>
            </div>
            <h1 {...stylex.props(styles.title)}>
              Your servers.
              <br />
              Your identity.
            </h1>
            <p {...stylex.props(styles.description)}>
              Sign in with your company account to manage short-lived SSH access. Your private key
              stays on your device.
            </p>
            <div {...stylex.props(styles.actions)}>
              <a href="/auth/login" {...stylex.props(styles.primaryLink)}>
                Sign in with SSB SSO
              </a>
            </div>
          </section>
          <AppearanceSelect />
        </main>
      </div>;
  return <div {...stylex.props(styles.shell)}>
      <a className="skip-link" href="#workspace-content">
        Skip to content
      </a>
      <header data-workspace-sidebar {...stylex.props(styles.header)}>
        <Link to="/" {...stylex.props(styles.brand)}>
          <img src="/ssbd-logo.png" width="44" height="44" alt="" />
          <span>
            SSBD<span {...stylex.props(styles.brandSubtitle)}>Access workspace</span>
          </span>
        </Link>
        <WorkspaceNavigation admin={me.data.admin} />

        <div {...stylex.props(styles.sidebarFooter)}>
          <AppearanceSelect />
          <span {...stylex.props(styles.identity)}>{me.data.email}</span>
          <Button label="Sign out" xstyle={styles.headerButton} size="sm" onClick={() => logout.mutate()} isLoading={logout.isPending} />
          {logout.error && <p role="alert" {...stylex.props(styles.small)}>
              {errorMessage(logout.error)}
            </p>}
        </div>
      </header>
      <div className="mobile-workspace-bar">
        <Link to="/" {...stylex.props(styles.brand)}>
          <img src="/ssbd-logo.png" width="32" height="32" alt="" />
          <span>SSBD Access</span>
        </Link>
        <Button label="Navigation" aria-haspopup="dialog" aria-expanded={navigationOpen} onClick={() => setNavigationOpen(true)} />
      </div>
      {navigationOpen && <Drawer title="Navigation" onClose={() => setNavigationOpen(false)}>
          <WorkspaceNavigation drawer admin={me.data.admin} onNavigate={() => setNavigationOpen(false)} />
          <div className="mobile-account">
            <AppearanceSelect />
            <span {...stylex.props(styles.identity)}>{me.data.email}</span>
            <Button label="Sign out" onClick={() => logout.mutate()} isLoading={logout.isPending} />
            {logout.error && <p role="alert" {...stylex.props(styles.small)}>
                {errorMessage(logout.error)}
              </p>}
          </div>
        </Drawer>}
      <main id="workspace-content" {...stylex.props(styles.main)}>
        <Outlet />
      </main>
    </div>;
}
function WorkspaceNavigation({
  admin,
  onNavigate,
  drawer = false
}: {
  admin: boolean;
  onNavigate?: () => void;
  drawer?: boolean;
}) {
  return <nav aria-label="Main navigation" {...stylex.props(drawer ? styles.drawerNavigation : styles.navigation)}>
      <p className="nav-section">Workspace</p>
      {admin && <>
          <Link onClick={onNavigate} to="/" activeProps={{
        ...stylex.props(styles.navLink, styles.active),
        "aria-current": "page"
      }} inactiveProps={stylex.props(styles.navLink)} activeOptions={{
        includeSearch: false,
        exact: true
      }}>
            <NavIcon name="Overview" />
            Overview
          </Link>
          <Link onClick={onNavigate} to="/servers" activeProps={{
        ...stylex.props(styles.navLink, styles.active),
        "aria-current": "page"
      }} inactiveProps={stylex.props(styles.navLink)} activeOptions={{
        includeSearch: false
      }}>
            <NavIcon name="Servers" />
            Servers
          </Link>
          <Link onClick={onNavigate} to="/cloud" search={{
        view: "inventory"
      }} activeProps={{
        ...stylex.props(styles.navLink, styles.active),
        "aria-current": "page"
      }} inactiveProps={stylex.props(styles.navLink)} activeOptions={{
        includeSearch: false
      }}>
            <NavIcon name="Cloud" />
            Cloud
          </Link>
          <Link onClick={onNavigate} to="/rules" search={{
        view: "groups"
      }} activeProps={{
        ...stylex.props(styles.navLink, styles.active),
        "aria-current": "page"
      }} inactiveProps={stylex.props(styles.navLink)} activeOptions={{
        includeSearch: false
      }}>
            <NavIcon name="Access" />
            Access
          </Link>
          <p className="nav-section">Review</p>
          <Link onClick={onNavigate} to="/recordings" activeProps={{
        ...stylex.props(styles.navLink, styles.active),
        "aria-current": "page"
      }} inactiveProps={stylex.props(styles.navLink)} activeOptions={{
        includeSearch: false
      }}>
            <NavIcon name="Recordings" />
            Recordings
          </Link>
          <Link onClick={onNavigate} to="/audit" activeProps={{
        ...stylex.props(styles.navLink, styles.active),
        "aria-current": "page"
      }} inactiveProps={stylex.props(styles.navLink)} activeOptions={{
        includeSearch: false
      }}>
            <NavIcon name="Activity" />
            Activity
          </Link>
        </>}
      <p className="nav-section">Your device</p>
      <Link onClick={onNavigate} to="/connect" activeProps={{
      ...stylex.props(styles.navLink, styles.active),
      "aria-current": "page"
    }} inactiveProps={stylex.props(styles.navLink)} activeOptions={{
      includeSearch: false
    }}>
        <NavIcon name="Connect" />
        Connect
      </Link>
    </nav>;
}